import { Cookie } from '@repo/tools'

const PASSWORD_ATTEMPTS_COOKIE = 'password_attempts'
const MAX_ATTEMPTS = 3
const COOKIE_EXPIRY_HOURS = 24

interface PasswordAttempts {
  count: number
  timestamp: number
  identifier: string
}

export const PasswordAttemptsManager = {
  /**
   * Get current password attempts for a specific identifier
   */
  getAttempts(identifier: string): PasswordAttempts | null {
    try {
      const cookieValue = Cookie.get(PASSWORD_ATTEMPTS_COOKIE)
      if (!cookieValue) return null

      const attempts: PasswordAttempts = JSON.parse(cookieValue)

      // Check if attempts are for the same identifier and not expired
      if (
        attempts.identifier === identifier &&
        !this.isExpired(attempts.timestamp)
      ) {
        return attempts
      }

      // If expired or different identifier, clear the cookie
      this.clearAttempts()
      return null
    } catch {
      this.clearAttempts()
      return null
    }
  },

  /**
   * Increment password attempts for a specific identifier
   */
  incrementAttempts(identifier: string): PasswordAttempts {
    const currentAttempts = this.getAttempts(identifier)

    const newAttempts: PasswordAttempts = {
      count: (currentAttempts?.count || 0) + 1,
      timestamp: Date.now(),
      identifier,
    }

    // Set cookie with 24 hour expiry
    const expiryDate = new Date()
    expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS)

    Cookie.set(PASSWORD_ATTEMPTS_COOKIE, JSON.stringify(newAttempts), {
      path: '/',
    })

    return newAttempts
  },

  /**
   * Clear password attempts (on successful login)
   */
  clearAttempts(): void {
    Cookie.remove(PASSWORD_ATTEMPTS_COOKIE, '/')
  },

  /**
   * Check if attempts are expired
   */
  isExpired(timestamp: number): boolean {
    const now = Date.now()
    const expiryTime = timestamp + COOKIE_EXPIRY_HOURS * 60 * 60 * 1000
    return now > expiryTime
  },

  /**
   * Check if user has reached max attempts
   */
  hasReachedMaxAttempts(identifier: string): boolean {
    const attempts = this.getAttempts(identifier)
    return attempts ? attempts.count >= MAX_ATTEMPTS : false
  },

  /**
   * Get remaining attempts
   */
  getRemainingAttempts(identifier: string): number {
    const attempts = this.getAttempts(identifier)
    if (!attempts) return MAX_ATTEMPTS
    return Math.max(0, MAX_ATTEMPTS - attempts.count)
  },

  /**
   * Get max attempts constant
   */
  getMaxAttempts(): number {
    return MAX_ATTEMPTS
  },
}
